import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { TourPriceOption, VisitPlaceContent, VisitPlacesTourPriceOption } from '@/types/tourBooking';
import {
  StartPickupIcon,
  CheckpointIcon
} from '@/components/SvgIcons';

const TimelineContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  position: 'relative',
  paddingLeft: theme.spacing(2),
}));

const TimelineItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLast',
})<{ isLast?: boolean }>(({ theme, isLast }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  position: 'relative',
  zIndex: 1,
  ...(!isLast && {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 24,
      bottom: -36,
      width: 1,
      backgroundColor: '#D0D0D0CC',
      zIndex: 0,
    },
  }),
}));

const TimelineHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const TimelineBullet = styled(Box)(({ theme }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: '#E5F6E8',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
  marginLeft: -12,
  zIndex: 1,
}));

const TimelineContent = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
}));

const TimelineImage = styled('img')({
  width: '100%',
  height: 160,
  objectFit: 'cover',
  borderRadius: 8,
  marginTop: 8,
});

interface TourTimelineProps {
  isBottomSheet: boolean;
  selectedTourPriceOption?: TourPriceOption | null;
}

const TourTimeline = ({ selectedTourPriceOption, isBottomSheet }: TourTimelineProps) => {
  const { t } = useTranslation();

  // Get title and shortContent from the selected tourPriceOption's contents
  const title = selectedTourPriceOption?.contents?.[0]?.title || '';
  const shortContent = selectedTourPriceOption?.contents?.[0]?.shortContent || '';

  // Get visitPlaces from the selected tourPriceOption
  const visitPlaces: VisitPlacesTourPriceOption[] = selectedTourPriceOption?.visitPlaces || [];

  if (!selectedTourPriceOption) {
    return (
      <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2 }}>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {t('common:noData')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: "0 16px 16px 16px" }}>

      <Box
        display={'flex'}
        flexDirection={'column'}
        position={isBottomSheet ? 'relative' : 'sticky'}
        top={isBottomSheet ? 'unset' : 0}
        pt={2}
        zIndex={99}
        sx={{ backgroundColor: '#fff' }}
      >
        {isBottomSheet ? null : (<Typography variant="h5" fontWeight={600} mb={1}>
          {t('common:service_package_details')}
        </Typography>)}

        {/* Title from contents */}
        {title && (
          <Typography variant="h3" fontWeight={600} mb={1} pt={1}>
            {title.charAt(0).toUpperCase() + title.slice(1)}
          </Typography>
        )}

        {/* Short content from contents */}
        {shortContent && (
          <Typography
            variant="body2"
            color="text.secondary"
            mb={3}
            dangerouslySetInnerHTML={{ __html: shortContent }}
            sx={{
              color: '#000000',
              '& p': {
                fontWeight: 400,
                padding: 0,
                margin: 0,
              }
            }}
          />
        )}
      </Box>

      {/* Schedule header - static localized */}
      <Typography variant="h4" fontWeight={600} marginY={2}>
        {t('common:schedule')}
      </Typography>

      {/* Timeline from visitPlaces */}
      {visitPlaces.length > 0 ? (
        <TimelineContainer>
          {visitPlaces.map((place, index) => {

            const placeContent = place.contents?.[0];

            const placeName = placeContent?.name || '';
            const placeDescription = placeContent?.shortDescription ?? placeContent?.description ?? '';
            const placeImage = place.thumbnailImage || undefined;

            return (
              <TimelineItem key={place.id || index} isLast={index === visitPlaces.length - 1}>
                <TimelineHeader>
                  <TimelineBullet sx={{ backgroundColor: index === 0 ? '#B8D9F9' : undefined }}>
                    {index === 0 ? <StartPickupIcon /> : <CheckpointIcon />}
                  </TimelineBullet>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {placeName}
                  </Typography>
                </TimelineHeader>
                <TimelineContent>
                  {placeDescription && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      dangerouslySetInnerHTML={{ __html: placeDescription }}
                      sx={{
                        color: '#000000',
                        '& p': {
                          fontWeight: 400,
                          padding: 0,
                          margin: 0,
                        }
                      }}
                    />
                  )}
                  {placeImage && <TimelineImage src={placeImage} alt={placeName} />}
                </TimelineContent>
              </TimelineItem>
            );
          })}
          {isBottomSheet ? <Box height={"64px"} /> : null}
        </TimelineContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('common:noData')}
        </Typography>
      )}
    </Box>
  );
};

export default TourTimeline;
